import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 古诗词赏析 - 竖屏「水墨意境」风格
 *
 * 核心亮点：
 * - 水墨晕染背景动画（SVG 径向渐变 + 扩散）
 * - 诗句逐字显现，仿毛笔书写节奏
 * - 字词注释浮出（平仄、典故、意象）
 * - 诗意插图区（山水 SVG 剪影 + 层叠视差）
 * - 赏析要点逐条展开
 * - 朝代/作者信息雅致排版
 *
 * 尺寸：1080x1920 竖屏
 */

interface AnnotatedWord {
  word: string;
  note?: string;
  highlight?: boolean;
}

interface PoetryLine {
  text: string;
  annotatedWords?: AnnotatedWord[];
}

interface PoetryInkTemplateProps {
  dynasty: string;
  author: string;
  title: string;
  lines: PoetryLine[];
  appreciation: string[];
  theme: string;
  imagery: string[];
  mood: string;
}

const DEFAULT_PROPS: PoetryInkTemplateProps = {
  dynasty: '唐',
  author: '李白',
  title: '静夜思',
  lines: [
    {
      text: '床前明月光',
      annotatedWords: [
        { word: '床', note: '井栏说 / 坐具说' },
        { word: '前明月光' },
      ],
    },
    {
      text: '疑是地上霜',
      annotatedWords: [
        { word: '疑', note: '似梦非梦间', highlight: true },
        { word: '是地上霜' },
      ],
    },
    {
      text: '举头望明月',
      annotatedWords: [
        { word: '举头', note: '从低到高的动作' },
        { word: '望明月' },
      ],
    },
    {
      text: '低头思故乡',
      annotatedWords: [
        { word: '低头', note: '与「举头」对仗' },
        { word: '思故乡', highlight: true },
      ],
    },
  ],
  appreciation: [
    '以「月光」起兴，从视觉到心理层层递进',
    '「疑」字写出朦胧半醒的真实状态',
    '举头—低头，一个动作完成思乡的情感转换',
    '全诗无一「愁」字，却字字含愁',
  ],
  theme: '望月思乡',
  imagery: ['明月', '霜', '故乡'],
  mood: '静谧中的深沉乡愁',
};

const InkBlot: React.FC<{ cx: number; cy: number; delay: number; maxR: number }> = ({ cx, cy, delay, maxR }) => {
  const frame = useCurrentFrame();
  const r = interpolate(frame, [delay, delay + 60], [0, maxR], { extrapolateRight: 'clamp' });
  const opacity = interpolate(frame, [delay, delay + 20, delay + 80, delay + 120], [0, 0.06, 0.04, 0.02], { extrapolateRight: 'clamp' });
  return <circle cx={cx} cy={cy} r={r} fill="#1a1a2e" opacity={opacity} />;
};

const MountainLayer: React.FC<{ y: number; progress: number; color: string; seed: number }> = ({ y, progress, color, seed }) => {
  const peaks = Array.from({ length: 8 }).map((_, i) => {
    const x = (i / 7) * 1080;
    const h = 40 + Math.sin(i * 1.7 + seed) * 60 + Math.cos(i * 0.8 + seed * 2) * 30;
    return { x, y: y - h };
  });

  const d = [
    `M0,${y + 50}`,
    ...peaks.map((p, i) => {
      if (i === 0) return `L${p.x},${p.y}`;
      const prev = peaks[i - 1];
      return `Q${(prev.x + p.x) / 2},${Math.min(prev.y, p.y) - 15} ${p.x},${p.y}`;
    }),
    `L1080,${y + 50} Z`,
  ].join(' ');

  return <path d={d} fill={color} opacity={progress * 0.6} />;
};

export const PoetryInkTemplate: React.FC<Partial<PoetryInkTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Phases
  const bgReveal = interpolate(frame, [0, 40], [0, 1], { extrapolateRight: 'clamp' });
  const titleProgress = spring({ frame: frame - 20, fps, config: { damping: 18, stiffness: 80 } });

  // Poetry lines timing
  const poemStart = 80;
  const lineInterval = 70;

  // Appreciation
  const appStart = poemStart + p.lines.length * lineInterval + 30;

  // Mountain parallax
  const mountainOffset = frame * 0.03;

  return (
    <AbsoluteFill
      style={{
        background: '#faf8f0',
        fontFamily: '"Noto Serif SC", "STSong", "SimSun", "Source Han Serif CN", serif',
        color: '#2c1810',
        overflow: 'hidden',
      }}
    >
      {/* Ink wash background SVG */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <InkBlot cx={200} cy={400} delay={0} maxR={350} />
        <InkBlot cx={800} cy={300} delay={10} maxR={280} />
        <InkBlot cx={500} cy={700} delay={20} maxR={400} />
        <InkBlot cx={900} cy={1200} delay={5} maxR={300} />
        <InkBlot cx={150} cy={1500} delay={15} maxR={320} />
      </svg>

      {/* Mountain silhouettes (parallax layers) */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <MountainLayer y={1600} progress={bgReveal} color="#d4c5a0" seed={1 + mountainOffset} />
        <MountainLayer y={1650} progress={bgReveal} color="#c4b590" seed={3 + mountainOffset * 0.7} />
        <MountainLayer y={1700} progress={bgReveal} color="#b4a580" seed={5 + mountainOffset * 0.4} />
      </svg>

      {/* Rice paper texture overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(139,119,80,0.03) 1px, transparent 0)',
          backgroundSize: '20px 20px',
        }}
      />

      {/* ========== HEADER: dynasty + author ========== */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -15}px)`,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 16,
            padding: '8px 28px',
            background: 'rgba(44,24,16,0.04)',
            borderRadius: 30,
            border: '1px solid rgba(44,24,16,0.08)',
          }}
        >
          <span style={{ fontSize: 40, color: '#8b7750' }}>〔{p.dynasty}〕</span>
          <span style={{ fontSize: 40, fontWeight: 600 }}>{p.author}</span>
        </div>

        <h1
          style={{
            fontSize: 80,
            fontWeight: 900,
            margin: '50px 0 0',
            letterSpacing: '0.15em',
          }}
        >
          {p.title}
        </h1>

        {/* Decorative ink line */}
        <div
          style={{
            width: 120 * titleProgress,
            height: 2,
            background: 'linear-gradient(90deg, transparent, #8b7750, transparent)',
            margin: '16px auto',
          }}
        />
      </div>

      {/* ========== POEM BODY ========== */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 80,
          right: 80,
        }}
      >
        {p.lines.map((line, lineIdx) => {
          const lineStart = poemStart + lineIdx * lineInterval;
          const charCount = line.text.length;

          return (
            <div key={lineIdx} style={{ marginBottom: 50, position: 'relative' }}>
              {/* Characters one by one */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                {line.text.split('').map((char, charIdx) => {
                  const charStart = lineStart + charIdx * 6;
                  const charOpacity = interpolate(frame, [charStart, charStart + 8], [0, 1], { extrapolateRight: 'clamp' });
                  const charScale = spring({
                    frame: frame - charStart,
                    fps,
                    from: 1.3,
                    to: 1,
                    config: { damping: 12, stiffness: 120 },
                  });
                  const charBlur = interpolate(frame, [charStart, charStart + 6], [4, 0], { extrapolateRight: 'clamp' });

                  // Check if this char is highlighted
                  let isHighlighted = false;
                  if (line.annotatedWords) {
                    let pos = 0;
                    for (const aw of line.annotatedWords) {
                      if (aw.highlight && charIdx >= pos && charIdx < pos + aw.word.length) {
                        isHighlighted = true;
                      }
                      pos += aw.word.length;
                    }
                  }

                  return (
                    <span
                      key={charIdx}
                      style={{
                        display: 'inline-block',
                        fontSize: 80,
                        fontWeight: 700,
                        opacity: charOpacity,
                        transform: `scale(${charScale})`,
                        filter: `blur(${charBlur}px)`,
                        color: isHighlighted ? '#8b3a3a' : '#2c1810',
                        textShadow: isHighlighted ? '0 0 20px rgba(139,58,58,0.2)' : 'none',
                        padding: '0 4px',
                      }}
                    >
                      {char}
                    </span>
                  );
                })}
              </div>

              {/* Annotations below */}
              {line.annotatedWords && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 10 }}>
                  {line.annotatedWords
                    .filter((aw) => aw.note)
                    .map((aw, awi) => {
                      const noteStart = lineStart + charCount * 6 + 10 + awi * 12;
                      const noteOpacity = interpolate(frame, [noteStart, noteStart + 15], [0, 1], { extrapolateRight: 'clamp' });
                      const noteY = interpolate(frame, [noteStart, noteStart + 15], [8, 0], { extrapolateRight: 'clamp' });

                      return (
                        <div
                          key={awi}
                          style={{
                            opacity: noteOpacity,
                            transform: `translateY(${noteY}px)`,
                            padding: '4px 14px',
                            background: 'rgba(139,119,80,0.06)',
                            border: '1px solid rgba(139,119,80,0.12)',
                            borderRadius: 8,
                            fontSize: 30,
                            color: '#8b7750',
                          }}
                        >
                          「{aw.word}」{aw.note}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ========== THEME & IMAGERY ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1150,
          left: 80,
          right: 80,
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          opacity: interpolate(frame, [appStart - 30, appStart - 10], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        <div
          style={{
            padding: '10px 24px',
            background: 'rgba(139,58,58,0.06)',
            border: '1px solid rgba(139,58,58,0.12)',
            borderRadius: 12,
          }}
        >
          <span style={{ fontSize: 40, color: '#8b7750' }}>主题：</span>
          <span style={{ fontSize: 40, fontWeight: 700, color: '#8b3a3a' }}>{p.theme}</span>
        </div>
        <div
          style={{
            padding: '10px 24px',
            background: 'rgba(139,119,80,0.06)',
            border: '1px solid rgba(139,119,80,0.12)',
            borderRadius: 12,
          }}
        >
          <span style={{ fontSize: 40, color: '#8b7750' }}>意境：</span>
          <span style={{ fontSize: 40, fontWeight: 700, color: '#5a4a2a' }}>{p.mood}</span>
        </div>
      </div>

      {/* Imagery tags */}
      <div
        style={{
          position: 'absolute',
          top: 1300,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 14,
          opacity: interpolate(frame, [appStart - 20, appStart], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        {p.imagery.map((img, i) => (
          <div
            key={i}
            style={{
              padding: '6px 18px',
              background: 'rgba(44,24,16,0.03)',
              borderRadius: 20,
              border: '1px solid rgba(44,24,16,0.06)',
              fontSize: 40,
              color: '#8b7750',
            }}
          >
            🏔 {img}
          </div>
        ))}
      </div>

      {/* ========== APPRECIATION ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1440,
          left: 80,
          right: 80,
        }}
      >
        <div
          style={{
            fontSize: 40,
            color: '#8b7750',
            letterSpacing: '0.2em',
            marginBottom: 16,
            opacity: interpolate(frame, [appStart, appStart + 10], [0, 1], { extrapolateRight: 'clamp' }),
          }}
        >
          ◆ 赏 析
        </div>
        {p.appreciation.map((line, i) => {
          const start = appStart + i * 30;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 20], [20, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                marginBottom: 18,
                opacity,
                transform: `translateX(${x}px)`,
              }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#8b7750',
                  marginTop: 10,
                  flexShrink: 0,
                  opacity: 0.5,
                }}
              />
              <span style={{ fontSize: 34, lineHeight: 1.6, color: '#3d2b1f' }}>{line}</span>
            </div>
          );
        })}
      </div>

      {/* Seal stamp (印章) */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          right: 80,
          opacity: interpolate(frame, [appStart + p.appreciation.length * 30, appStart + p.appreciation.length * 30 + 20], [0, 0.7], { extrapolateRight: 'clamp' }),
          transform: `rotate(-5deg)`,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            border: '3px solid #c23a2a',
            borderRadius: 6,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 28,
            fontWeight: 900,
            color: '#c23a2a',
            letterSpacing: '0.05em',
          }}
        >
          {p.author.substring(0, 2)}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #8b7750, #c23a2a)',
        }}
      />
    </AbsoluteFill>
  );
};
